import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { styled, useTheme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles';
import {
  Autocomplete,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle, Box, MobileStepper, Typography,
} from '@mui/material'

import DateForm from '@components/DateForm'
import AttachFileForm from '@components/AttachFileForm'

import useMediaQuery from '@mui/material/useMediaQuery'

import SwipeableViews from 'react-swipeable-views';
import moment from "moment";

import {
  adminGetCompanyNameList,
  clearCompanyNameList,
} from '@store/admin/company'

const dotSize = {
  width: '13px',
  height: '13px',
};

const useStyles = makeStyles({
  dot: dotSize,
  dotActive: dotSize
})

const StyledDialogContent = styled(DialogContent)({
  backgroundColor: '#fdfdfd',
  borderTop: '1px solid #eee',
  borderBottom: '1px solid #eee'
})

const DialogForm = styled('div')({
  padding: '10px 0'
})

const StepBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center'
})
const PreButton = styled(Button)({
  position: 'absolute',
  left: 10
})

const DialogTextField = styled(TextField)({
  backgroundColor: '#fff',
  margin: '10px 0'
})

const initData = {
  companyId: '',
  companyName: '',
  position: '',
  name: '',
  birth: null,
  signExpectDate: null,
  resignExpectDate: null,
  corporateRegistrationName: '',
  corporateRegistrationPath: '',
  corporateRegistrationKey: null,
  attachInfo: {
    personalInfoAgreeName: '',
    personalInfoAgreePath: '',
    personalInfoAgreeKey: null,
    noloanAgreeName: '',
    noloanAgreePath: '',
    noloanAgreeKey: null,
  }
}

const Modal = ({
  data,
  open,
  completeModal
}) => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const classes = useStyles()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const {companyNameList} = useSelector(store => store.companyAdmin)
  const [ form, setForm ] = useState(initData)
  const [ isConfirm, setIsConfirm ] = useState(false)
  const [ activeStep, setActiveStep ] = useState(0)
  const maxSteps = 2
  const [ tempForm, setTempForm ] = useState({})

  const displayInfo = useMemo(() => {
    const isNew = !!!data
    return {
      companyId: {
        type: 'selectKey',
        value: '회사아이디',
      },
      companyName: {
        type: isNew ? 'suggest' : 'text',
        key: 'companyId',
        value: '회사명',
        required: true,
        isEdit: isNew,
      },
      position: {
        type: 'text',
        value: '직책',
        required: false,
        isEdit: true,
      },
      name: {
        type: 'text',
        value: '성명',
        required: true,
        isEdit: true,
      },
      birth: {
        type: 'date',
        value: '생년월일',
        required: true,
        isEdit: true,
      },
      signExpectDate: {
        type: 'date',
        value: '선임(예정)일',
        required: true,
        isEdit: true,
      },
      resignExpectDate: {
        type: 'date',
        value: '사임(예정)일',
        required: false,
        isEdit: true,
      },
      corporateRegistrationName: {
        type: 'attach',
        value: '법인등기부등본',
        path: 'corporateRegistrationPath',
        key: 'corporateRegistrationKey',
        required: false,
        isEdit: true,
      },
      corporateRegistrationPath: {
        type: 'attach_path',
        value: '법인등기부등본 파일경로',
        required: false,
      },
      corporateRegistrationKey: {
        type: 'attach_key',
        value: '법인등기부등본 파일키',
        required: false,
      },
      attachInfo: {
        personalInfoAgreeName: {
          type: 'attach',
          value: '개인정보 동의서',
          path: 'personalInfoAgreePath',
          key: 'personalInfoAgreeKey',
          required: false,
          isEdit: true,
        },
        personalInfoAgreePath: {
          type: 'attach_path',
          value: '개인정보 동의서 파일 경로',
          required: false,
        },
        personalInfoAgreeKey: {
          type: 'attach_key',
          value: '개인정보 동의서 파일키',
          required: false,
        },
        noloanAgreeName: {
          type: 'attach',
          value: '대출 불가 확약서',
          path: 'noloanAgreePath',
          key: 'noloanAgreeKey',
          required: false,
          isEdit: true,
        },
        noloanAgreePath: {
          type: 'attach_path',
          value: '대출 불가 확약서 파일 경로',
          required: false,
        },
        noloanAgreeKey: {
          type: 'attach_key',
          value: '대출 불가 확약서 파일키',
          required: false,
        },
      }
    }
  }, [data])

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  }

  const handleStepChange = (step) => {
    setActiveStep(step);
  }

  const handleClose = (e, reason) => {
    if (reason === 'backdropClick') {
      return
    }
    setIsConfirm(false)
    completeModal(false)
    setForm(initData)
    setActiveStep(0)
  }

  const handleChangeData = (key, value) => {
    const updateForm = {}
    if (value && typeof value === 'object' && Object.keys(value).length) {
      Object.keys(value).forEach(key => {
        updateForm[key] = value[key]
      })
    } else {
      updateForm[key] = value
    }
    setForm(Object.assign({}, form, updateForm))
  }

  const addFile = (key, addedFile, isAttachInfo = true) => {
    setTempForm({
      ...tempForm,
      [key]: addedFile,
    })

    if (isAttachInfo) {
      setForm({
        ...form,
        attachInfo: {
          ...form.attachInfo,
          [key]: addedFile.name
        },
      })
    } else {
      setForm({
        ...form,
        [key]: addedFile.name
      })
    }
  }

  const deleteFile = (key, isAttachInfo = true) => {
    setTempForm({
      ...tempForm,
      [key]: null,
    })

    if (isAttachInfo) {
      const updateFile = {
        attachInfo: {...form.attachInfo}
      }
      updateFile.attachInfo[key] = null
      updateFile.attachInfo[displayInfo.attachInfo[key].path] = null
      updateFile.attachInfo[displayInfo.attachInfo[key].key] = null
      setForm(Object.assign({}, form, updateFile))
    } else {
      const updateFile = {}
      updateFile[key] = null
      updateFile[displayInfo[key].path] = null
      updateFile[displayInfo[key].key] = null
      setForm(Object.assign({}, form, updateFile))
    }
  }

  const handleUpdateData = () => {
    setIsConfirm(true)

    const { companyId, name, birth, signExpectDate } = form

    if (companyId && name && birth && signExpectDate) {
      const completeForm = {
        ...form,
        birth: moment(form.birth).add(9, 'h'),
        signExpectDate: moment(form.signExpectDate).add(9, 'h'),
        resignExpectDate: moment(form.resignExpectDate).add(9, 'h'),
        attachInfo: form.attachInfo,
        companyName: form.companyName,
      }

      const memberForm = new FormData()
      memberForm.append('member', JSON.stringify(completeForm))

      //TODO: 고치고싶은 부분...
      for (const key in tempForm) {
        if (tempForm[key] != null) {
          memberForm.append(key.replaceAll('Name', ''), tempForm[key])
        }
      }

      completeModal(true, memberForm, completeForm.companyMemberCode)

      setIsConfirm(false)
      setForm(initData)
      setActiveStep(0)
      setTempForm({})
    } else {
      setActiveStep(0)
    }
  }

  useEffect(() => {
    setForm(data || initData)
  }, [ data, open ])

  useEffect(() => {
    if (data) return;
    dispatch(adminGetCompanyNameList())
  }, [dispatch, data])

  useEffect(() => {
    return () => {
      dispatch(clearCompanyNameList())
    }
  }, [dispatch])

  return (
    <Dialog
      fullScreen={fullScreen}
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown
      aria-labelledby="관리자 변경"
      fullWidth={true}
      maxWidth={'sm'}
    >
      <DialogTitle id="responsive-dialog-title">
        특수관계인 {!!data ? '변경' : '등록'}
      </DialogTitle>
      <SwipeableViews
        index={activeStep}
        onChangeIndex={handleStepChange}
        enableMouseEvents={false}
      >
        <StyledDialogContent>
          <DialogForm>
            {
              Object.keys(initData).map((key, index) => {
                return (
                  <React.Fragment key={index}>
                    {
                      displayInfo[key].type === 'date' &&
                      <DateForm
                        dataKey={key}
                        displayName={displayInfo}
                        form={form}
                        error={isConfirm && (displayInfo[key].required && !form[key])}
                        handleChangeData={handleChangeData}
                        required={displayInfo[key].required}
                        disabled={!displayInfo[key].isEdit}
                        fullWidth={true}
                        customStyle={{mt: 1.5, mb: 1.5}}
                      />
                    }
                    {
                      displayInfo[key].type === 'text' &&
                      <DialogTextField
                        label={displayInfo[key].value}
                        value={form[key]}
                        fullWidth
                        required={displayInfo[key].required}
                        error={isConfirm && (displayInfo[key].required && !form[key])}
                        autoFocus={!index}
                        size={'small'}
                        onChange={(e) => handleChangeData(key, e.target.value)}
                        disabled={!displayInfo[key].isEdit}
                      />
                    }
                    {
                      displayInfo[key].type === 'suggest' && 
                      <Autocomplete
                        sx={{mb: 1}}
                        disablePortal
                        getOptionLabel={option => option.companyName}
                        options={companyNameList}
                        onChange={(e, newValue) => handleChangeData(key, newValue)}
                        renderInput={(params) => <TextField 
                          {...params} 
                          label={displayInfo[key].value} 
                          fullWidth 
                          size="small" 
                          required={displayInfo[key].required}
                          error={isConfirm && (displayInfo[key].required && !form[key])}
                        />}
                      />
                    }
                  </React.Fragment>
                )
              })
            }
          </DialogForm>
        </StyledDialogContent>
        <StyledDialogContent>
          <DialogForm>
            {
              Object.keys(initData.attachInfo).map((key, index) => {
                return (
                  <React.Fragment key={index}>
                    {
                      displayInfo.attachInfo[key].type === 'attach' &&
                      <AttachFileForm
                        title={displayInfo.attachInfo[key].value}
                        type={key}
                        name={form.attachInfo && form.attachInfo[key]}
                        file={form.attachInfo && form.attachInfo[displayInfo.attachInfo[key].path]}
                        addFile={addFile}
                        deleteFile={deleteFile}
                      />
                    }
                  </React.Fragment>
                )
              })
            }
            {
              Object.keys(initData).map((key, index) => {
                return (
                  <React.Fragment key={index}>
                    {
                      displayInfo[key].type === 'attach' &&
                      <AttachFileForm
                        title={displayInfo[key].value}
                        type={key}
                        name={form[key]}
                        file={form[displayInfo[key].path]}
                        addFile={(type, file) => addFile(type, file, false)}
                        deleteFile={(type) => deleteFile(type, false)}
                      />
                    }
                  </React.Fragment>
                )
              })
            }
          </DialogForm>
        </StyledDialogContent>
      </SwipeableViews>
      <StepBox>
        <MobileStepper
          steps={maxSteps}
          position="static"
          activeStep={activeStep}
          classes={{
            dot: classes.dot,
            dotActive: classes.dotActive,
          }}
        />
        <Typography variant="h5">
          {activeStep + 1}/{maxSteps}
        </Typography>
      </StepBox>

      <DialogActions>
        {
          activeStep === maxSteps - 1 &&
          <>
            <Button variant="contained" disableElevation onClick={handleUpdateData}>
              {!!data ? '변경' : '등록'}
            </Button>
            <PreButton variant="contained" disableElevation color="secondary" onClick={handleBack} disabled={activeStep === 0}>
              이전
            </PreButton>
          </>
        }
        {
          activeStep !== maxSteps - 1 &&
          <Button variant="contained" disableElevation color="secondary" onClick={handleNext} disabled={activeStep === maxSteps - 1}>
            다음
          </Button>
        }
        <Button onClick={handleClose}>
          닫기
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default Modal